import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { LogoUpload } from "@/components/admin/LogoUpload";

interface Organization {
  id: string;
  name: string;
  category: string | null;
  questionnaire_id?: string | null;
  created_at: string;
}

interface OrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organization?: Organization | null;
  onSave: (orgData: Partial<Organization> & { questionnaire_id: string; logo_url?: string }, reason: string) => void;
}

interface QuestionnaireOption {
  questionnaire_id: string;
  label: string;
}

export const OrganizationDialog = ({
  open,
  onOpenChange,
  organization,
  onSave
}: OrganizationDialogProps) => {
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    questionnaireId: "",
    logoUrl: ""
  });
  const [reason, setReason] = useState("");
  const [questionnaireOptions, setQuestionnaireOptions] = useState<QuestionnaireOption[]>([]);
  const { toast } = useToast();

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || "",
        category: organization.category || "",
        questionnaireId: organization.questionnaire_id || "",
        logoUrl: ""
      });
    } else {
      setFormData({
        name: "",
        category: "",
        questionnaireId: "",
        logoUrl: ""
      });
    }
    setReason("");
  }, [organization, open]);

  const handleCategoryChange = async (value: string) => {
    setFormData(prev => ({ ...prev, category: value, questionnaireId: "" }));
    
    if (value === "Telecom (Fixed/ISP)") {
      try {
        const { data, error } = await supabase
          .from('v_org_category_questionnaires')
          .select('questionnaire_id, label')
          .eq('category', value);

        if (error) {
          console.error('Error fetching questionnaires:', error);
          toast({
            title: "Error",
            description: "Failed to load questionnaire options",
            variant: "destructive"
          });
          setQuestionnaireOptions([]);
          return;
        }

        setQuestionnaireOptions(data || []);
        
        // Default to telecom_v2 if available, otherwise first option
        const defaultOption = (data || []).find(d => d.questionnaire_id === 'telecom_v2')?.questionnaire_id
                            || (data && data[0]?.questionnaire_id) || '';
        setFormData(prev => ({ ...prev, questionnaireId: defaultOption }));
      } catch (error) {
        console.error('Error fetching questionnaires:', error);
        setQuestionnaireOptions([]);
      }
    } else {
      setQuestionnaireOptions([]);
    }
  };

  const handleSave = () => {
    if (!formData.name.trim() || !formData.category.trim() || !reason.trim()) return;
    
    // For "Telecom (Fixed/ISP)", require questionnaire selection
    if (formData.category === "Telecom (Fixed/ISP)" && !formData.questionnaireId) {
      toast({
        title: "Validation Error",
        description: "Please select a questionnaire for Telecom (Fixed/ISP) category",
        variant: "destructive"
      });
      return;
    }

    const payload: any = {
      name: formData.name,
      category: formData.category,
      questionnaire_id: formData.questionnaireId || null,
    };
    if (formData.logoUrl) payload.logo_url = formData.logoUrl;
    
    onSave(payload, reason);
    onOpenChange(false);
  };

  const isValid = formData.name.trim() && formData.category.trim() && reason.trim() && 
    (formData.category !== "Telecom (Fixed/ISP)" || formData.questionnaireId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {organization ? "Edit Organization" : "Create Organization"}
          </DialogTitle>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Organization Name</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="Enter organization name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="category">Category *</Label>
            <Select 
              value={formData.category} 
              onValueChange={handleCategoryChange}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select category (required)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Telecom (Fixed/ISP)">Telecommunications</SelectItem>
                <SelectItem value="Media">Media</SelectItem>
                <SelectItem value="Private Equity">Private Equity</SelectItem>
                <SelectItem value="Data Center">Data Center</SelectItem>
                <SelectItem value="Pre-Investment Triage">Pre-Investment Triage</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {questionnaireOptions.length > 0 && (
            <div className="space-y-2">
              <Label htmlFor="questionnaire">Questionnaire *</Label>
              <Select
                value={formData.questionnaireId}
                onValueChange={(value) => setFormData(prev => ({ ...prev, questionnaireId: value }))}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select questionnaire" />
                </SelectTrigger>
                <SelectContent>
                  {questionnaireOptions.map(option => (
                    <SelectItem key={option.questionnaire_id} value={option.questionnaire_id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Logo Upload (for edit, pathArgs could be built if we had parent context) */}
          <LogoUpload
            onUploaded={(path) => setFormData(prev => ({ ...prev, logoUrl: path }))}
            onFileSelected={(file) => {
              // For edit forms, hold file - caller handles upload with proper path
              const reader = new FileReader();
              reader.onload = () => {}; // preview handled internally
              reader.readAsDataURL(file);
            }}
            currentLogoUrl={organization ? (organization as any).logo_url : undefined}
          />

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for this action</Label>
            <Textarea
              id="reason"
              placeholder="Explain why you're creating/editing this organization..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            {organization ? "Update" : "Create"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};